import { useState, useEffect, useRef } from 'react';
import {
  fetchAdminImages, deleteImage,
  fetchAdminStops, createStop, updateStop, deleteStop,
  fetchAdminTours,
} from '../common/api';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

function authHeader() {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const emptyStopForm = { tour_id: '', time: '', place: '', sort_order: '' };
const emptyImageForm = { tour_id: '', alt_text: '', sort_order: '' };

export default function Stations() {
  const [tab, setTab] = useState('images');

  // 공통
  const [tours, setTours] = useState([]);
  const [tourFilter, setTourFilter] = useState('all');

  // 이미지
  const [images, setImages] = useState([]);
  const [imgLoading, setImgLoading] = useState(true);
  const [imgModal, setImgModal] = useState(false);
  const [imgForm, setImgForm] = useState(emptyImageForm);
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [imgSaving, setImgSaving] = useState(false);
  const fileInputRef = useRef(null);

  // 정류장
  const [stops, setStops] = useState([]);
  const [stopLoading, setStopLoading] = useState(true);
  const [stopModal, setStopModal] = useState(null);
  const [stopForm, setStopForm] = useState(emptyStopForm);
  const [editStopId, setEditStopId] = useState(null);
  const [stopSaving, setStopSaving] = useState(false);

  useEffect(() => {
    fetchAdminTours().then(setTours).catch(console.error);
  }, []);

  useEffect(() => {
    if (tab === 'images') loadImages();
    if (tab === 'stations') loadStops();
  }, [tab, tourFilter]);

  async function loadImages() {
    setImgLoading(true);
    try {
      const data = await fetchAdminImages();
      setImages(tourFilter === 'all' ? data : data.filter(i => i.tour_id === tourFilter));
    } catch (err) {
      console.error(err);
    } finally {
      setImgLoading(false);
    }
  }

  async function loadStops() {
    setStopLoading(true);
    try {
      const data = await fetchAdminStops(tourFilter !== 'all' ? tourFilter : null);
      setStops(data);
    } catch (err) {
      console.error(err);
    } finally {
      setStopLoading(false);
    }
  }

  // ── 이미지 ──
  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImgFile(file);
    setImgPreview(URL.createObjectURL(file));
  }

  function openImgModal() {
    setImgForm(emptyImageForm);
    setImgFile(null);
    setImgPreview(null);
    setImgModal(true);
  }

  function closeImgModal() {
    setImgModal(false);
    setImgFile(null);
    setImgPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleImageAdd(e) {
    e.preventDefault();
    if (!imgFile) { alert('Please select an image file'); return; }
    if (!imgForm.tour_id) { alert('Please select a tour'); return; }

    setImgSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', imgFile);
      formData.append('tour_id', imgForm.tour_id);
      formData.append('alt_text', imgForm.alt_text);
      formData.append('sort_order', imgForm.sort_order || '0');

      const res = await fetch(`${BASE_URL}/upload/image?tour_id=${imgForm.tour_id}`, {
        method: 'POST',
        headers: authHeader(),
        body: formData,
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Upload failed');

      closeImgModal();
      loadImages();
    } catch (err) {
      alert(err.message);
    } finally {
      setImgSaving(false);
    }
  }

  async function handleImageDelete(id) {
    if (!window.confirm('Delete this image?')) return;
    try {
      // S3 + DB 동시 삭제
      const res = await fetch(`${BASE_URL}/upload/image/${id}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Delete failed');
      loadImages();
    } catch (err) {
      alert(err.message);
    }
  }

  // ── 정류장 ──
  function openAddStop() {
    setStopForm({ ...emptyStopForm, tour_id: tourFilter !== 'all' ? tourFilter : '' });
    setEditStopId(null);
    setStopModal('add');
  }

  function openEditStop(s) {
    setStopForm({ tour_id: s.tour_id, time: s.time, place: s.place, sort_order: s.sort_order });
    setEditStopId(s.id);
    setStopModal('edit');
  }

  async function handleStopSubmit(e) {
    e.preventDefault();
    setStopSaving(true);
    try {
      const data = { time: stopForm.time, place: stopForm.place, sort_order: Number(stopForm.sort_order) || 0 };
      if (stopModal === 'add') await createStop({ ...data, tour_id: stopForm.tour_id });
      else await updateStop(editStopId, data);
      setStopModal(null);
      loadStops();
    } catch (err) {
      alert(err.message);
    } finally {
      setStopSaving(false);
    }
  }

  async function handleStopDelete(id) {
    if (!window.confirm('Delete this stop?')) return;
    try {
      await deleteStop(id);
      loadStops();
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Images & Stops</h1>

      {/* 탭 + 투어 필터 */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex gap-2">
          {['images', 'stations'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t ? 'bg-[#E8876A] text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
            >
              {t === 'images' ? 'Images' : 'Stops'}
            </button>
          ))}
        </div>
        <select
          value={tourFilter}
          onChange={(e) => setTourFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
        >
          <option value="all">All Tours</option>
          {tours.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
        </select>
      </div>

      {/* ── 이미지 탭 ── */}
      {tab === 'images' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">{images.length} images</p>
            <button
              onClick={openImgModal}
              className="px-4 py-2 bg-[#E8876A] text-white text-sm font-medium rounded-lg hover:bg-[#D96F52] transition-colors"
            >
              + Upload Image
            </button>
          </div>

          {imgLoading ? (
            <div className="py-16 text-center text-gray-400 text-sm">Loading...</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((img) => (
                <div key={img.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="h-36 bg-gray-100 overflow-hidden">
                    <img src={img.image_url} alt={img.alt_text || ''} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <p className="text-xs font-semibold text-[#E8876A]">{img.tour_title}</p>
                    <p className="text-xs text-gray-600 mt-0.5 truncate">{img.alt_text || '—'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">order: {img.sort_order}</p>
                    <button
                      onClick={() => handleImageDelete(img.id)}
                      className="mt-2 text-xs text-red-500 hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {images.length === 0 && (
                <div className="col-span-4 py-12 text-center text-gray-400 text-sm">No images found.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── 정류장 탭 ── */}
      {tab === 'stations' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">{stops.length} stops</p>
            <button
              onClick={openAddStop}
              className="px-4 py-2 bg-[#E8876A] text-white text-sm font-medium rounded-lg hover:bg-[#D96F52] transition-colors"
            >
              + Add Stop
            </button>
          </div>

          {stopLoading ? (
            <div className="py-16 text-center text-gray-400 text-sm">Loading...</div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tour</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Order</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Time</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Place</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stops.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-[#E8876A] font-medium">{s.tour_title}</td>
                      <td className="px-6 py-4 text-sm text-gray-400">{s.sort_order}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{s.time}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-800">{s.place}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-3">
                          <button onClick={() => openEditStop(s)} className="text-xs text-blue-600 hover:underline">Edit</button>
                          <button onClick={() => handleStopDelete(s.id)} className="text-xs text-red-500 hover:underline">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {stops.length === 0 && (
                <div className="py-12 text-center text-gray-400 text-sm">No stops found.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── 이미지 업로드 모달 ── */}
      {imgModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Upload Image</h2>
            <form onSubmit={handleImageAdd} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tour</label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
                  value={imgForm.tour_id}
                  onChange={(e) => setImgForm({ ...imgForm, tour_id: e.target.value })}
                  required
                >
                  <option value="">Select tour</option>
                  {tours.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              </div>

              {/* 파일 업로드 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image File</label>
                <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#E8876A] transition-colors overflow-hidden">
                  {imgPreview ? (
                    <img src={imgPreview} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center">
                      <p className="text-sm text-gray-400">Click to select image</p>
                      <p className="text-xs text-gray-300 mt-1">JPG, PNG, WEBP · Max 10MB</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
                {imgFile && (
                  <p className="text-xs text-gray-400 mt-1 truncate">{imgFile.name}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alt Text</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
                    value={imgForm.alt_text}
                    onChange={(e) => setImgForm({ ...imgForm, alt_text: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
                    value={imgForm.sort_order}
                    onChange={(e) => setImgForm({ ...imgForm, sort_order: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeImgModal} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={imgSaving || !imgFile} className="flex-1 px-4 py-2 bg-[#E8876A] text-white text-sm rounded-lg hover:bg-[#D96F52] disabled:opacity-50">
                  {imgSaving ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── 정류장 추가/수정 모달 ── */}
      {stopModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">
              {stopModal === 'add' ? 'Add Stop' : 'Edit Stop'}
            </h2>
            <form onSubmit={handleStopSubmit} className="space-y-4">
              {stopModal === 'add' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tour</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
                    value={stopForm.tour_id}
                    onChange={(e) => setStopForm({ ...stopForm, tour_id: e.target.value })}
                    required
                  >
                    <option value="">Select tour</option>
                    {tours.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
                    placeholder="09:00"
                    value={stopForm.time}
                    onChange={(e) => setStopForm({ ...stopForm, time: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
                    value={stopForm.sort_order}
                    onChange={(e) => setStopForm({ ...stopForm, sort_order: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Place</label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8876A]"
                  placeholder="Seoul Pickup"
                  value={stopForm.place}
                  onChange={(e) => setStopForm({ ...stopForm, place: e.target.value })}
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setStopModal(null)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={stopSaving} className="flex-1 px-4 py-2 bg-[#E8876A] text-white text-sm rounded-lg hover:bg-[#D96F52] disabled:opacity-50">
                  {stopSaving ? 'Saving...' : stopModal === 'add' ? 'Add' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}